import { useNavigate } from "@tanstack/react-router";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useMarketplaceConnections } from "@/api/use-marketplace-connections";
import { useCan, useIsAdmin } from "@/shared/auth";
import { FilterChips } from "@/shared/components/FilterChips";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";
import { Button } from "@/shared/ui/button";
import { Spinner } from "@/shared/ui/spinner";

import { useAttentionCounts } from "../api/use-attention-counts";
import { useBulkExport } from "../api/use-order-actions";
import { type AttentionReason, type OrderFilters, useOrders } from "../api/use-orders";
import { BulkBar } from "../components/BulkBar";
import { FilterDrawer } from "../components/FilterDrawer";
import { OrderFilters as OrderFiltersComp } from "../components/OrderFilters";
import { OrdersTable } from "../components/OrdersTable";
import { OrderSyncFreshness } from "../components/OrderSyncFreshness";
import { ResolveDialog } from "../components/ResolveDialog";
import { SyncMenu } from "../components/SyncMenu";
import { buildChips } from "../lib/filter-chips";
import {
  hasActiveFilters,
  mergeFilter,
  resetPage,
  type FilterUpdate,
} from "../lib/order-url-state";
import { topReviewReason } from "../lib/review-flags";
import type { Order } from "../lib/types";

interface OrdersPageProps {
  urlFilter: OrderFilters;
  onUrlFilterChange: (next: OrderFilters) => void;
}

// Display order for the "Needs attention" sub-chips — matches both
// AttentionCounts.by_reason and status-taxonomy.json's review_reason order.
const ATTENTION_REASON_ORDER: AttentionReason[] = ["oversold", "buyer_cancel_requested"];

export function OrdersPage({ urlFilter, onUrlFilterChange }: OrdersPageProps): JSX.Element {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const canWriteOrders = useCan("orders:write");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [resolveTarget, setResolveTarget] = useState<{
    order: Order;
    reason: AttentionReason;
  } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: connections = [] } = useMarketplaceConnections();
  // Every shop with an id, whatever its status — filtering orders by a shop
  // that was disconnected last week is the point of a history filter (BE spec
  // D5). The promotion form is the surface that disables non-active shops.
  const sellers = connections.filter((c): c is typeof c & { shop_id: string } => !!c.shop_id);

  const { data, isLoading } = useOrders(urlFilter);
  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  // The "All" tab shows the unfiltered-by-attention total even while the
  // Needs-attention tab is active (mockup A.2) — one extra 1-row query.
  const { attention: _attention, ...unfiltered } = urlFilter;
  const { data: allData } = useOrders({ ...unfiltered, limit: 1, offset: 0 });
  const allTotal = urlFilter.attention === undefined ? total : (allData?.total ?? total);

  const { data: attentionCounts } = useAttentionCounts(urlFilter);
  const attentionTotal = attentionCounts?.total ?? 0;
  const attentionTab: "all" | "attention" = urlFilter.attention !== undefined ? "attention" : "all";

  const limit = urlFilter.limit ?? 20;
  const offset = urlFilter.offset ?? 0;

  const { mutate: bulkExport } = useBulkExport();

  function handleFilterChange(updates: FilterUpdate): void {
    onUrlFilterChange(resetPage(mergeFilter(urlFilter, updates)));
    setSelectedIds(new Set());
  }

  function handleClear(): void {
    onUrlFilterChange({});
    setSelectedIds(new Set());
  }

  const chips = buildChips(
    urlFilter,
    handleFilterChange,
    (key, opts) => (opts === undefined ? t(key) : t(key, opts)),
    { sellers, stageLabel: (s) => t(`st_${s}`) },
  );

  function handleToggleSelect(id: string): void {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleToggleAll(): void {
    const allIds = orders.map((o) => o.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  }

  function handleView(saleOrderId: string): void {
    void navigate({ to: "/orders/$saleOrderId", params: { saleOrderId } });
  }

  function handleResolve(id: string): void {
    const target = orders.find((o) => o.id === id);
    const reason = target ? topReviewReason(target.reviewReasons) : null;
    if (target && reason) setResolveTarget({ order: target, reason });
  }

  function handleAttentionTab(tab: "all" | "attention"): void {
    handleFilterChange({ attention: tab === "attention" ? "any" : undefined });
  }

  function handleAttentionChip(reason: AttentionReason | "any"): void {
    handleFilterChange({ attention: reason });
  }

  function handleBulkExport(): void {
    const ids = [...selectedIds];
    bulkExport(ids, {
      onSuccess: () => {
        toast.success(t("t_bulk_export", { n: ids.length }));
        setSelectedIds(new Set());
      },
      onError: () => toast.error(t("t_bulk_fail")),
    });
  }

  function handleBulkClear(): void {
    setSelectedIds(new Set());
  }

  function handleExport(): void {
    void navigate({ to: "/export-orders" });
  }

  // Orders intentionally overrides the shared Pagination default ([25,50,100]) to
  // [20,50,100] because the orders list defaults to 20 rows/page. Other pages keep 25.
  const pager = (
    <Pagination
      total={total}
      offset={offset}
      limit={limit}
      pageSizeOptions={[20, 50, 100]}
      onChange={({ offset: nextOffset, limit: nextLimit }) =>
        onUrlFilterChange({ ...urlFilter, offset: nextOffset, limit: nextLimit })
      }
    />
  );

  return (
    <div data-testid="orders-page">
      <PageHeader
        title={t("title_orders")}
        actions={
          <>
            <OrderSyncFreshness />
            {isAdmin && <SyncMenu />}
            <Button onClick={handleExport} variant="default" size="sm">
              {t("btn_export")}
            </Button>
          </>
        }
      />

      <div
        role="tablist"
        aria-label={t("attention.tablist_label")}
        className="mb-3 flex flex-wrap gap-2"
      >
        <button
          type="button"
          role="tab"
          aria-selected={attentionTab === "all"}
          data-testid="attention-tab-all"
          onClick={() => handleAttentionTab("all")}
          className={[
            "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
            attentionTab === "all"
              ? "bg-[var(--brand-50)] text-[var(--brand-700)]"
              : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]",
          ].join(" ")}
        >
          {t("attention.all")} {allTotal}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={attentionTab === "attention"}
          data-testid="attention-tab-needs"
          onClick={() => handleAttentionTab("attention")}
          className={[
            "rounded-full px-3 py-1.5 text-sm font-medium transition-colors",
            attentionTab === "attention"
              ? "bg-[var(--intent-warning-bg)] text-[var(--intent-warning-fg)]"
              : "text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]",
          ].join(" ")}
        >
          {t("attention.tab")} {attentionTotal}
        </button>
      </div>

      {attentionTab === "attention" && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="attention-subchips">
          {attentionTotal > 0 && (
            <button
              type="button"
              aria-pressed={urlFilter.attention === "any"}
              data-testid="attention-chip-any"
              onClick={() => handleAttentionChip("any")}
              className={[
                "rounded-full border px-2.5 py-1 text-xs font-semibold",
                urlFilter.attention === "any"
                  ? "border-[var(--brand-500)] text-[var(--brand-700)]"
                  : "border-[var(--premium-border)] text-[var(--text-secondary)]",
              ].join(" ")}
            >
              {t("attention.any")} {attentionTotal}
            </button>
          )}
          {ATTENTION_REASON_ORDER.map((reason) => {
            const count = attentionCounts?.by_reason[reason] ?? 0;
            if (count <= 0) return null;
            return (
              <button
                key={reason}
                type="button"
                aria-pressed={urlFilter.attention === reason}
                data-testid={`attention-chip-${reason}`}
                onClick={() => handleAttentionChip(reason)}
                className={[
                  "rounded-full border px-2.5 py-1 text-xs font-semibold",
                  urlFilter.attention === reason
                    ? "border-[var(--brand-500)] text-[var(--brand-700)]"
                    : "border-[var(--premium-border)] text-[var(--text-secondary)]",
                ].join(" ")}
              >
                {t(`reason.${reason}`)} {count}
              </button>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-16" data-testid="orders-loading">
          <Spinner size="lg" label={t("loading_orders")} />
        </div>
      ) : (
        <>
          <OrderFiltersComp
            filter={urlFilter}
            onChange={handleFilterChange}
            onClear={handleClear}
            onOpenFilters={() => setDrawerOpen(true)}
          />
          {chips.length > 0 && (
            <div className="mb-3">
              <FilterChips chips={chips} onClearAll={handleClear} />
            </div>
          )}
          {pager}
          <OrdersTable
            orders={orders}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleAll={handleToggleAll}
            onView={handleView}
            onResolve={handleResolve}
            selectable={canWriteOrders}
            canResolve={canWriteOrders}
            hasFilters={hasActiveFilters(urlFilter)}
            onClearFilters={handleClear}
          />
          {pager}
          {canWriteOrders && (
            <BulkBar
              count={selectedIds.size}
              onExport={handleBulkExport}
              onClear={handleBulkClear}
            />
          )}
        </>
      )}

      {resolveTarget && (
        <ResolveDialog
          open
          order={resolveTarget.order}
          reason={resolveTarget.reason}
          onClose={() => setResolveTarget(null)}
        />
      )}

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        filter={urlFilter}
        sellers={sellers}
        onApply={handleFilterChange}
      />
    </div>
  );
}
